import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Bell, FileText, CheckCircle, Mail, Loader2 } from 'lucide-react';
import { formatDateTime } from '../../lib/utils';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(notifications.map((n) =>
        n.id === id ? { ...n, lu: true } : n
      ));
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleMarkAllAsRead = async () => {
    setMarkingAll(true);
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(notifications.map((n) => ({ ...n, lu: true })));
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setMarkingAll(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'nouveau_fichier':
        return <FileText className="w-5 h-5 text-blue-600" />;
      case 'statut_fichier':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'nouveau_contact':
        return <Mail className="w-5 h-5 text-amber-600" />;
      default:
        return <Bell className="w-5 h-5 text-slate-600" />;
    }
  };

  const getNotificationMessage = (notification) => {
    switch (notification.type) {
      case 'nouveau_fichier':
        return `${notification.payload.utilisateur} a téléversé "${notification.payload.fichier_nom}" dans le projet "${notification.payload.projet_nom}"`;
      case 'statut_fichier':
        return `Le statut de "${notification.payload.fichier_nom}" a été changé à "${notification.payload.nouveau_statut}"`;
      case 'nouveau_contact':
        return `Nouvelle demande de contact de ${notification.payload.nom} (${notification.payload.email})`;
      default:
        return 'Nouvelle notification';
    }
  };

  const unreadCount = notifications.filter((n) => !n.lu).length;

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Notifications</h1>
          <p className="text-slate-600">
            {unreadCount > 0 ? `${unreadCount} non lue(s)` : 'Toutes les notifications sont lues'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" onClick={handleMarkAllAsRead} disabled={markingAll}>
            {markingAll ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Tout marquer comme lu
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {notifications.length === 0 ? (
            <div className="py-12 text-center">
              <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Aucune notification</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-4 p-4 hover:bg-slate-50 transition-colors ${
                    !notification.lu ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => !notification.lu && handleMarkAsRead(notification.id)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notification.lu ? 'font-medium' : ''}`}>
                      {getNotificationMessage(notification)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatDateTime(notification.created_at)}
                    </p>
                  </div>
                  {!notification.lu && (
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2" />
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;
